import { createExportSnapshot, normalizeProject, validateSnapshot } from './domain/index.js';
import {
  ArchAIProjectRepository,
  AssetRepository,
  LocalDraftRepository,
  MockProjectRepository
} from './repositories/index.js';
import { createDeliverablesStore } from './store/index.js';
import { createDeliverablesView } from './ui/index.js';
import { openReportPreview } from './app/report-preview.js';

const exporterLoaders = {
  pptx: () => import('./exporters/pptx.js').then(module => module.exportPptx),
  pdf: () => import('./exporters/pdf.js').then(module => module.exportPdf),
  docx: () => import('./exporters/docx.js').then(module => module.exportDocx),
  zip: () => import('./exporters/archive.js').then(module => module.exportImageArchive)
};

function sourceOptions() {
  const query = new URLSearchParams(location.search);
  const projectId = query.get('projectId') || '';
  const useApi = query.get('source') === 'api' || Boolean(projectId);
  return { projectId, useApi };
}

function setStartupError(error) {
  document.getElementById('loadingState')?.classList.add('hidden');
  document.getElementById('errorState')?.classList.remove('hidden');
  const message = document.getElementById('errorMessage');
  if (message) message.textContent = error?.message || '成果汇总初始化失败。';
  document.getElementById('chapterList')?.setAttribute('aria-busy', 'false');
}

function exportMessage(report) {
  if (!report.valid) return `无法导出：${report.errors.join('；')}`;
  if (report.warnings.length) return `可以导出，检查提示：${report.warnings.join('；')}`;
  return '导出检查通过。图片将在导出时读取并嵌入文件。';
}

function bindPrototypeShell(view) {
  for (const button of document.querySelectorAll('.stage-item:not([data-stage="deliverables"])')) {
    button.addEventListener('click', () => view.showToast('此独立原型只开放 06 成果汇总。'));
  }
  for (const id of ['openProjects', 'openProjectsTop']) {
    document.getElementById(id)?.addEventListener('click', () => view.showToast('当前使用独立 mock 项目，可通过 URL 参数接入真实项目。'));
  }
  document.querySelectorAll('.side-utility,.top-actions>.icon-btn,.top-actions>.secondary-btn').forEach(button => {
    button.addEventListener('click', () => view.showToast('该控件继承主站外观，在独立 06 原型中不改变项目数据。'));
  });
}

async function boot() {
  const { projectId, useApi } = sourceOptions();
  const projectRepository = useApi ? new ArchAIProjectRepository() : new MockProjectRepository();
  const sourceSnapshot = await projectRepository.getProjectSnapshot(projectId);
  const documentModel = normalizeProject(sourceSnapshot);
  const draftRepository = new LocalDraftRepository();
  const draft = await draftRepository.load(documentModel.project.id);
  const store = createDeliverablesStore({ document: documentModel, draft });
  const assetRepository = new AssetRepository();
  let saveTimer = 0;
  let saveQueue = Promise.resolve();

  const view = createDeliverablesView({
    store,
    async onPreviewImage(item) {
      return assetRepository.getBlob(item);
    },
    onPreviewReport(documentValue) {
      openReportPreview(documentValue);
    },
    async onOpenExport(payload) {
      const snapshot = createExportSnapshot(payload.state);
      const report = validateSnapshot(snapshot);
      if (payload.phase === 'open') {
        payload.setStatus(exportMessage(report), report.valid ? (report.warnings.length ? 'warning' : 'success') : 'error');
        return;
      }
      if (!report.valid) throw new Error(report.errors.join('；'));
      const loadExporter = exporterLoaders[payload.format];
      if (!loadExporter) throw new Error(`暂不支持 ${payload.format} 导出。`);
      payload.setStatus(`正在载入 ${payload.format.toUpperCase()} 导出模块`);
      const exporter = await loadExporter();
      const result = await exporter(snapshot, assetRepository, progress => {
        payload.setStatus(`${progress.message} ${progress.percent}%`);
      });
      payload.setStatus(`${result.fileName} 已生成（${Math.max(1, Math.round(result.size / 1024))} KB）`, 'success');
      return result;
    },
    async onReset() {
      await draftRepository.clear(documentModel.project.id);
    }
  });

  store.subscribe(state => {
    window.clearTimeout(saveTimer);
    const label = document.getElementById('autosaveLabel');
    if (label) label.textContent = '正在保存编排草稿';
    saveTimer = window.setTimeout(() => {
      saveQueue = saveQueue.then(() => draftRepository.save(state.draft)).then(() => {
        if (label) label.textContent = `草稿已保存，修订 ${state.revision}`;
      }).catch(error => view.showToast(error?.message || '草稿保存失败', 'error'));
    }, 320);
  });

  function flushDraftBeforeExit() {
    window.clearTimeout(saveTimer);
    if (!store.getState().revision) return;
    void draftRepository.save(store.getState().draft).catch(() => {});
  }

  bindPrototypeShell(view);
  window.addEventListener('pagehide', flushDraftBeforeExit);
  window.addEventListener('beforeunload', flushDraftBeforeExit);
  if (import.meta.env.DEV) {
    globalThis.__ARCHAI_DELIVERABLES__ = Object.freeze({ store, view });
  }
}

boot().catch(setStartupError);
